import { announce } from '@react-aria/live-announcer';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useIsAuthenticating } from '@/context/auth/IsAuthenticatingProvider';
import { useAnalytics } from '@/hooks/analytics';
import useIsCalmSleepApp from '@/hooks/useIsCalmSleepApp';
import { setUser } from '@/store/user/actions';
import getUrl from '@/utils/getUrl';
import { clearClassicUserCookies } from '@/utils/privacyCookies';

const messages = defineMessages({
	loggedOutAnnouncement: {
		id: 'login.loggedOutAnnouncement',
		defaultMessage: 'You have successfully logged out',
		description: 'Notification to screen reader that user has logged out',
	},
});

export function useLoginForm(): {
	onLogout: () => void;
	onModeToggle: () => void;
} {
	const dispatch = useDispatch();
	const { authPrompt, setAuthPrompt } = useAuthPrompt();
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const { setIsAuthenticating } = useIsAuthenticating();

	const isCalmSleepApp = useIsCalmSleepApp();

	function handleLogout(): void {
		clearClassicUserCookies();
		dispatch(setUser(null));
		announce(formatMessage(messages.loggedOutAnnouncement));
	}

	function onLogout(): void {
		setIsAuthenticating(true);
		handleLogout();
		// eslint-disable-next-line @next/next/no-location-assign-relative-destination
		window.location.href = `${getUrl('www')}${isCalmSleepApp ? '/calm-sleep-app' : ''}/auth/logout`;
	}

	function onModeToggle(): void {
		const newMode = authPrompt === 'signup' ? 'login' : 'signup';

		logEvent({
			eventName: 'Login Form : Mode : Toggled',
			eventProps: {
				mode: newMode,
			},
		});
		setAuthPrompt(newMode);
	}

	return { onLogout, onModeToggle };
}
